import React, { useState } from 'react';
import moment from 'moment';
import { Grid, Typography, Chip } from '@mui/material';
import Modal from 'react-bootstrap/Modal';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import { toast } from 'react-toastify';
import ApiService from "../../functions/apiService";
import { Col, Row } from 'react-bootstrap';

const PAYCODE_STYLES = {
    OVERTIME:   { bg: '#FEF3C7', color: '#92400E' },
    CALLBACK:   { bg: '#DBEAFE', color: '#1E40AF' },
    LATE_CALL:  { bg: '#EDE9FE', color: '#5B21B6' },
    ADJUSTMENT: { bg: '#D1FAE5', color: '#065F46' },
    DEFAULT:    { bg: '#F3F4F6', color: '#374151' },
};

const paycodeStyle = (code) => PAYCODE_STYLES[code] || PAYCODE_STYLES.DEFAULT;

const PaycodePill = ({ value }) => {
    if (!value) return <span style={{ color: '#9CA3AF' }}>—</span>;
    const { bg, color } = paycodeStyle(value);
    return (
        <Chip label={value} size="small" sx={{
            backgroundColor: bg, color, fontWeight: 600,
            fontSize: '11px', height: '22px', borderRadius: '6px',
        }} />
    );
};

const HrsPill = ({ value }) => {
    if (value === undefined || value === null || value === 'N/A')
        return <span style={{ color: '#9CA3AF' }}>—</span>;
    const hrs = parseFloat(value);
    const bg    = hrs >= 8 ? '#FEE2E2' : hrs >= 4 ? '#FEF3C7' : '#ECFDF5';
    const color = hrs >= 8 ? '#991B1B' : hrs >= 4 ? '#92400E' : '#065F46';
    return (
        <Chip label={`${hrs % 1 === 0 ? hrs.toFixed(0) : hrs}`} size="small" sx={{
            backgroundColor: bg, color, fontWeight: 700,
            fontSize: '12px', height: '22px', borderRadius: '6px', minWidth: '32px',
        }} />
    );
};

const RunPill = ({ value }) => {
    const v = String(value ?? '');
    if (!v || v === '0' || v.toLowerCase() === 'n/a' || v.toLowerCase() === 'na')
        return <span style={{ color: '#9CA3AF' }}>—</span>;
    return (
        <Chip label={v} size="small" variant="outlined" sx={{
            borderColor: '#D1D5DB', color: '#374151', fontWeight: 500,
            fontSize: '11px', height: '22px', borderRadius: '6px',
        }} />
    );
};

const Field = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
        </Typography>
        <div style={{ fontSize: '13px', color: '#111827', fontFamily: 'karla, sans-serif', display: 'flex', alignItems: 'center' }}>
            {children}
        </div>
    </div>
);

const GridView = ({ data, refresh }) => {
    const [deletModal, setDeleteModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const handleDeleteClick = (record) => {
        setSelectedRecord(record);
        setDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!selectedRecord) return;
        try {
            const response = await ApiService.post('/payroll/delete-payrollRecord', {
                Shift_Number: selectedRecord.shift_number,
                Shift_Date: selectedRecord.shift_date
            });
            toast.success(response.message);
            refresh();
            setDeleteModal(false);
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <>
            <Grid container spacing={2}>
                {data.map((row, index) => (
                    <Grid item xs={12} key={index}>
                        <div style={{
                            border: '1px solid #E5E7EB',
                            borderRadius: '10px',
                            backgroundColor: '#fff',
                            overflow: 'hidden',
                        }}>
                            {/* Card header */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 16px',
                                backgroundColor: '#F9FAFB',
                                borderBottom: '1px solid #E5E7EB',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#111827', fontFamily: 'karla' }}>
                                        {row.employee_name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', color: '#6B7280', fontFamily: 'karla' }}>
                                        {row.shift_date ? moment(row.shift_date).format('MM/DD/YYYY') : '—'}
                                    </Typography>
                                </div>
                                <img src='/images/delete.svg' style={{ cursor: 'pointer', opacity: 0.6 }}
                                    onClick={() => handleDeleteClick(row)} />
                            </div>

                            {/* Card body — field grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                                gap: '12px 20px',
                                padding: '12px 16px',
                            }}>
                                <Field label="Run #">
                                    <RunPill value={row.assignment_code} />
                                </Field>
                                <Field label="Reason">
                                    {row.detail_text || '—'}
                                </Field>
                                <Field label="Time">
                                    {row.start_time?.slice(0, 5) || '—'} – {row.end_time?.slice(0, 5) || '—'}
                                </Field>
                                <Field label="Hours">
                                    <HrsPill value={row.hours} />
                                </Field>
                                <Field label="Pay Code">
                                    <PaycodePill value={row.pay_code} />
                                </Field>
                                <Field label="Created By">
                                    {row.created_by || '—'}
                                </Field>
                                <Field label="Timestamp">
                                    {row.create_time ? moment(row.create_time).format('MM/DD/YYYY h:mm A') : '—'}
                                </Field>
                            </div>
                        </div>
                    </Grid>
                ))}
            </Grid>

            <Modal show={deletModal} onHide={() => setDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Record</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ color: '#555555' }}>
                        Are you sure to delete this Record?
                    </div>
                    <Row>
                        <Col md='auto'>
                            <ButtonTheme type='submit' variant="filled" color='white' className='mt-4' onClick={() => setDeleteModal(false)}>
                                Cancel
                            </ButtonTheme>
                        </Col>
                        <Col md='auto'>
                            <ButtonTheme type='submit' variant="filled" color='primary' className='mt-4' onClick={handleDelete}>
                                Delete
                            </ButtonTheme>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default GridView;
